import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { ExerciseTemplateDto } from '@therapist/shared/models/exercise/exercise-template.dto';
import { ExerciseTemplateApiService } from '@therapist/shared/services/api/exercise-template-api.service';
import { ExercisesForDiagnoseModel } from '@therapist/shared/models/exercise/exercises-for-diagnose.model';
import { environment } from '../../../../environments/environment';

@Injectable({providedIn: 'root'})
export class ExerciseStateService {

  readonly #exerciseApiService = inject(ExerciseTemplateApiService);

  // All available exercises in an array
  #allExercises: WritableSignal<ExerciseTemplateDto[]> = signal([]);

  // All available exercises in an array, grouped by Diagnose
  #allExercisesGroupedByDiagnosis: WritableSignal<ExercisesForDiagnoseModel[]> = signal([]);

  readonly allExercisesWithVariantsFlat = computed(() => {
    return [
      ...this.#allExercises(),
      ...this.#allExercises().flatMap(x => x.variants)
    ]
  });

  // User-selected exercises for a therapy plan
  #selectedExercises: WritableSignal<ExerciseTemplateDto[]> = signal([]);

  #isExerciseListLoaded: WritableSignal<boolean> = signal(false);

  constructor() {
    if (environment.enableStateLogging) {
      window.setInterval(() => {
        console.log('selected exercises: ', this.#selectedExercises());
        console.log('all exercises: ', this.#allExercises());
      }, 2500);
    }
  }

  /**
   * returns the selectedExercises from the state
   */
  getSelectedExercises(): Signal<ExerciseTemplateDto[]> {
    return this.#selectedExercises.asReadonly();
  }

  /**
   * removes all selected exercises
   */
  removeSelectedExercises(): void {
    this.#selectedExercises.set([]);
  }

  /**
   * adds an exercise to the selected exercises
   */
  addSelectedExercise(exercise: ExerciseTemplateDto): void {
    this.#selectedExercises.update(current => [
      ...current,
      exercise
    ]);
  }

  /**
   *  removes a selected exercise from the selected exercises list
   */
  removeSelectedExercise(id: string): void {
    this.#selectedExercises.update(current => current.filter(e => e.id !== id));
  }

  /**
   * loads exercises for a list of Joints and adds them to the state
   */
  loadExercisesByJointIds(jointIds: string[], addToSelection = false): void {
    this.#isExerciseListLoaded.set(false);

    this.#exerciseApiService
      .getByJointIds(jointIds)
      .subscribe((exercises: ExerciseTemplateDto[]) => {
        this.addToAllExercises(exercises);

        if (addToSelection) {
          exercises.forEach(e => this.addSelectedExercise(e));
        }

        this.#isExerciseListLoaded.set(true);
      });
  }

  /**
   * adds an exercise to allExercises list
   */
  private addToAllExercises(exercises: ExerciseTemplateDto[]): void {
    this.#allExercises.update(current => [
      ...current,
      ...exercises
    ]);

    this.#allExercisesGroupedByDiagnosis.update(current => [
      ...exercises.map(e => ({
        exercises: [e]
      }))
    ]);
  }

  getAllExercises(): Signal<ExerciseTemplateDto[]> {
    return this.#allExercises.asReadonly();
  }

  removeAllExercises(): void {
    this.#allExercises.set([]);
    this.#allExercisesGroupedByDiagnosis.set([]);
    this.removeSelectedExercises();
  }

  removeAll(): void {
    this.removeAllExercises();
    this.removeSelectedExercises();
  }

  getIsExerciseListLoaded(): Signal<boolean> {
    return this.#isExerciseListLoaded.asReadonly();
  }
}
